import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getRentalBlockedDates,
    createRentalBlockedDate,
    deleteRentalBlockedDate,
} from "../rentalService";
import {
    STOP_SALE_DAY_LABELS,
    DayToggleRow,
    StopSaleList,
    inputClass,
} from "../rentalStopSaleUi";

const RentalBlockedDates = ({ rentalId }) => {
    const queryClient = useQueryClient();
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [reason, setReason] = useState("");
    const [days, setDays] = useState([true, true, true, true, true, true, true]);
    const [formError, setFormError] = useState("");
    const [deletingId, setDeletingId] = useState(null);

    const { data: blockedDatesData, isLoading } = useQuery({
        queryKey: ["rentalBlockedDates", rentalId],
        queryFn: () => getRentalBlockedDates(rentalId),
        enabled: !!rentalId,
    });

    const blockedDates = blockedDatesData?.data || [];

    const createMutation = useMutation({
        mutationFn: createRentalBlockedDate,
        onSuccess: () => {
            queryClient.invalidateQueries(["rentalBlockedDates", rentalId]);
            setFromDate("");
            setToDate("");
            setReason("");
            setDays([true, true, true, true, true, true, true]);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteRentalBlockedDate,
        onSuccess: () => {
            queryClient.invalidateQueries(["rentalBlockedDates", rentalId]);
            setDeletingId(null);
        },
        onError: () => setDeletingId(null),
    });

    const isInvalidDateRange = Boolean(fromDate && toDate && toDate < fromDate);
    const canSubmit =
        fromDate && toDate && !isInvalidDateRange && days.some(Boolean) && !createMutation.isPending;

    const handleFromDateChange = (value) => {
        setFormError("");
        setFromDate(value);
        if (value && toDate && toDate < value) {
            setToDate(value);
        }
    };

    const handleToDateChange = (value) => {
        if (fromDate && value && value < fromDate) {
            setFormError("To date cannot be earlier than From date.");
            return;
        }
        setFormError("");
        setToDate(value);
    };

    const handleAdd = async () => {
        setFormError("");
        if (!fromDate) {
            setFormError("From date is required.");
            return;
        }
        if (!toDate) {
            setFormError("To date is required.");
            return;
        }
        if (fromDate > toDate) {
            setFormError("To date cannot be earlier than From date.");
            return;
        }
        if (!days.some(Boolean)) {
            setFormError("Select at least one applicable weekday.");
            return;
        }

        const reasonTrim = String(reason || "").trim();
        if (reasonTrim.length > 500) {
            setFormError("Reason must be at most 500 characters.");
            return;
        }

        const applicable_days = {
            sun: Boolean(days[0]),
            mon: Boolean(days[1]),
            tue: Boolean(days[2]),
            wed: Boolean(days[3]),
            thu: Boolean(days[4]),
            fri: Boolean(days[5]),
            sat: Boolean(days[6]),
        };

        try {
            await createMutation.mutateAsync({
                rental_item_id: rentalId,
                start_date: fromDate,
                end_date: toDate,
                reason: reasonTrim,
                applicable_days,
            });
        } catch (e) {
            setFormError(e?.response?.data?.message || e?.message || "Failed to save stop sale.");
        }
    };

    const handleDelete = (id) => {
        if (!window.confirm("Remove this stop sale? Bookings will be allowed again for these dates.")) {
            return;
        }
        setDeletingId(id);
        deleteMutation.mutate(id);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center rounded-xl border border-gray-100 bg-slate-50/40 py-16">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <i className="fi fi-rr-spinner animate-spin" aria-hidden />
                    Loading stop sales…
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 flex gap-3">
                <i className="fi fi-rr-info text-amber-600 mt-0.5 shrink-0" aria-hidden />
                <p className="text-xs text-amber-900/80 leading-relaxed">
                    Optional step — block specific dates when this item should not be bookable. Use{" "}
                    <span className="font-semibold">Skip</span> at the bottom if you do not need stop sales.
                </p>
            </div>

            <section className="rounded-xl border border-gray-100 bg-slate-50/40 p-5 space-y-5">
                <div>
                    <h3 className="text-sm font-bold text-gray-900">Add stop sale</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Choose a date range and the weekdays it applies to within that range.
                    </p>
                </div>

                {formError ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {formError}
                    </div>
                ) : null}

                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            From date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            required
                            value={fromDate}
                            onChange={(e) => handleFromDateChange(e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div className="hidden md:flex pb-2.5 text-gray-300 font-medium">→</div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            To date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            required
                            disabled={!fromDate}
                            min={fromDate || undefined}
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            className={`${inputClass} disabled:bg-gray-100 disabled:cursor-not-allowed ${
                                isInvalidDateRange ? "border-red-500 ring-1 ring-red-200" : ""
                            }`}
                        />
                        {isInvalidDateRange ? (
                            <p className="text-xs text-red-600 mt-1">To date cannot be earlier than From date.</p>
                        ) : null}
                    </div>
                </div>

                <DayToggleRow
                    days={days}
                    onToggle={(idx) => setDays((prev) => prev.map((v, i) => (i === idx ? !v : v)))}
                    onSelectAll={() => setDays(STOP_SALE_DAY_LABELS.map(() => true))}
                    onClearAll={() => setDays(STOP_SALE_DAY_LABELS.map(() => false))}
                />

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Reason <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                        type="text"
                        value={reason}
                        onChange={(e) => {
                            setFormError("");
                            setReason(e.target.value);
                        }}
                        placeholder="e.g. Maintenance, seasonal closure"
                        maxLength={500}
                        className={inputClass}
                    />
                </div>

                <div className="flex justify-end pt-1">
                    <button
                        type="button"
                        onClick={handleAdd}
                        disabled={!canSubmit}
                        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {createMutation.isPending ? (
                            <>
                                <i className="fi fi-rr-spinner animate-spin text-xs" aria-hidden />
                                Adding…
                            </>
                        ) : (
                            <>
                                <i className="fi fi-rr-plus text-xs" aria-hidden />
                                Add stop sale
                            </>
                        )}
                    </button>
                </div>
            </section>

            <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-bold text-gray-900">
                        Active stop sales
                        {blockedDates.length ? (
                            <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
                                {blockedDates.length}
                            </span>
                        ) : null}
                    </h3>
                </div>
                <StopSaleList
                    items={blockedDates}
                    onDelete={handleDelete}
                    isDeleting={deleteMutation.isPending}
                    deletingId={deletingId}
                />
            </section>
        </div>
    );
};

export default RentalBlockedDates;
